/**
 * Pure tag-range logic used by the "Select to End Tag" button. Split out so
 * it can be tested without mocking TinyMCE or the DOM.
 *
 * The input `flat` is body.textContent from TinyMCE — Radius tags appear as
 * literal `<r:…>` characters inside text nodes (TinyMCE only entity-encodes
 * them when serializing HTML), so offsets into `flat` map directly back to
 * (text node, offset) pairs via a text-node walk.
 */

export interface TagRange {
  name: string;
  start: number;
  end: number;
  selfClose: boolean;
}

const TAG_RE = /<r:([a-zA-Z_][a-zA-Z0-9_:]*)\b[^<>]*?(\/?)>/g;

/**
 * Locate the open `<r:name>` tag that the user "means" — either the tag inside
 * the current selection (preferred), or the closest preceding open tag to the
 * caret. Returns null if nothing plausibly tag-shaped is near the caret.
 */
export function findOpenTag(flat: string, caret: number, selectedText?: string): TagRange | null {
  // If the user highlighted text that contains a tag, honor that — but locate
  // it by the instance nearest the caret so duplicated tags earlier in the
  // document don't pull the result to the wrong place.
  if (selectedText && selectedText.indexOf('<r:') >= 0) {
    const selStart = nearestIndexOf(flat, selectedText, caret);
    if (selStart >= 0) {
      TAG_RE.lastIndex = 0;
      const m = TAG_RE.exec(selectedText);
      if (m) {
        return {
          name: m[1],
          start: selStart + m.index,
          end: selStart + m.index + m[0].length,
          selfClose: m[2] === '/',
        };
      }
    }
  }

  TAG_RE.lastIndex = 0;
  let best: RegExpExecArray | null = null;
  let m: RegExpExecArray | null;
  while ((m = TAG_RE.exec(flat))) {
    if (m.index > caret) break;
    best = m;
  }
  if (!best) return null;
  return { name: best[1], start: best.index, end: best.index + best[0].length, selfClose: best[2] === '/' };
}

/**
 * Starting from `open`, walk forward over tags with the same name, tracking
 * nesting depth, and return the flat-text offset just past the matching
 * `</r:name>`. Returns -1 if the tag is unclosed. Self-closing inner tags
 * with the same name don't affect depth.
 */
export function findEndOfBlock(flat: string, open: TagRange): number {
  if (open.selfClose) return open.end;
  const re = new RegExp('<(/)?r:' + open.name + '\\b[^<>]*?(/)?>', 'g');
  re.lastIndex = open.end;
  let depth = 1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(flat))) {
    const isClose = m[1] === '/';
    const isSelf = m[2] === '/';
    if (isSelf) continue;
    if (isClose) {
      depth--;
      if (depth === 0) return m.index + m[0].length;
    } else {
      depth++;
    }
  }
  return -1;
}

/**
 * Given the caret position and (optional) currently selected text, return the
 * full flat-text range to highlight — from the open tag through its matching
 * close. Returns null when no usable open tag is found.
 */
export function rangeForTagUnder(flat: string, caret: number, selectedText?: string): { start: number; end: number; name: string; selfClose: boolean } | null {
  const open = findOpenTag(flat, caret, selectedText);
  if (!open) return null;
  const end = findEndOfBlock(flat, open);
  if (end < 0) return null;
  return { start: open.start, end, name: open.name, selfClose: open.selfClose };
}

function nearestIndexOf(haystack: string, needle: string, near: number): number {
  let idx = -1;
  let best = -1;
  let bestDist = Infinity;
  while ((idx = haystack.indexOf(needle, idx + 1)) !== -1) {
    const dist = Math.abs(idx - near);
    if (dist < bestDist) {
      bestDist = dist;
      best = idx;
    }
  }
  return best;
}
